import Vue from "vue";
import axios from "axios";
import VueI18n from 'vue-i18n';
import store from "@/stores/store";
import moment from 'moment';
import messages from "@/i18n/messages"; // ou autre source

require('moment/locale/fr')

// Constants for available languages and their codes
// Short codes must match top-level keys in messages.js (en / fr).
const LANGUAGES = {
  fr: 'fr',
  en: 'en',
};

const CONTENTFUL_LOCALES = {
  fr: 'fr-FR',
  en: 'en-US',
};

/**
 * Reduce a stored language to a supported short code ('fr-FR' -> 'fr').
 * @param {string|null|undefined} value
 * @returns {string|null} The supported short code, or null when unsupported.
 */
function toSupportedShortCode(value) {
  if (!value) {
    return null;
  }
  const shortCode = String(value).split(/[-_]/)[0];
  return LANGUAGES[shortCode] ? shortCode : null;
}

/**
 * Function to determine the best language for the user
 * @returns {string} The best language code (e.g., 'fr' or 'en')
 */
function determineBestLanguage() {
  // 1. Check URL parameter 'l'
  const urlParams = new URLSearchParams(window.location.search);
  const langParam = urlParams.get('l');
  if (langParam && LANGUAGES[langParam]) {
    return langParam;
  }

  // 2. Check the global user_language variable
  const userLanguage = toSupportedShortCode(store.getters["session/current_user_language"]);
  if (userLanguage) {
    return userLanguage;
  }

  // 3. Check the entity language. A user language left unset means "never chosen",
  // not "wants the browser language": the company they belong to always has one.
  const entityLanguage = toSupportedShortCode(
    store.getters["session/current_entity"]?.language
  );
  if (entityLanguage) {
    return entityLanguage;
  }

  // 4. Check browser languages
  const browserLanguages = navigator.languages || [navigator.language || navigator.userLanguage];
  for (let i = 0; i < browserLanguages.length; i++) {
    const browserLang = browserLanguages[i].split('-')[0]; // Extract language code (e.g., 'en' from 'en-US')
    if (LANGUAGES[browserLang]) {
      return browserLang;
    }
  }

  // 5. Default to French, matching `fallbackLocale` below, the API `LANGUAGE_CODE`
  // and the `Entity.language` default.
  return 'fr';
}

const locale = determineBestLanguage();

store.commit("session/updateActiveLocale", CONTENTFUL_LOCALES[locale] || locale);

Vue.use(VueI18n);
const i18n = new VueI18n({
  locale: LANGUAGES[locale],
  fallbackLocale: 'fr',
  messages,
});
Vue.prototype.i18n = i18n;
Vue.prototype.$translate = function (key, ...values) {
  if (this.$i18n && typeof this.$t === "function") {
    return this.$t(key, ...values);
  }
  const i18n = this.i18n;
  if (!i18n) {
    console.error("VueI18n instance not found. Ensure VueI18n is properly initialized.");
    return key;
  }
  return i18n._t.apply(i18n, [key, i18n.locale, i18n._getMessages(), this, ...values]);
};

const availableLanguages = {
  en: 'English',
  fr: 'Français',
};

Vue.prototype.$availableLanguages = availableLanguages;

Vue.use(require('vue-moment'), { moment });
Vue.moment().locale(CONTENTFUL_LOCALES[locale] || locale);
axios.defaults.headers.common[
  "Accept-Language"
] = locale;

export default i18n;
